"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type ProcessStep = {
  number: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const steps: ProcessStep[] = [{
  number: "01.",
  title: "Strategic Briefing",
  description: "We begin by deeply analyzing your organizational culture, technical requirements, and the specific leadership traits needed for long-term success.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_briefing.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_briefing.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_briefing.png-wvc-sizes",
  alt: "Consultant reviewing corporate strategy documents in a modern office"
}, {
  number: "02.",
  title: "Market Mapping",
  description: "Our researchers identify high-performing talent across the industry, utilizing proprietary data to pinpoint candidates who align with your vision.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_mapping.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_mapping.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_mapping.png-wvc-sizes",
  alt: "Digital data visualization of professional networks and talent pools"
}, {
  number: "03.",
  title: "Targeted Outreach",
  description: "We engage potential candidates with precision and discretion, acting as a professional extension of your brand to attract top-tier interest.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_outreach.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_outreach.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_outreach.png-wvc-sizes",
  alt: "Professional communication interface showing targeted outreach efforts"
}, {
  number: "04.",
  title: "Rigorous Assessment",
  description: "Candidates undergo a multi-stage evaluation process, including competency-based interviews and cultural fit assessments to ensure alignment.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_assessment.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_assessment.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_assessment.png-wvc-sizes",
  alt: "Executive interview taking place in a professional boardroom setting"
}, {
  number: "05.",
  title: "Final Integration",
  description: "We facilitate the final selection and offer negotiation, ensuring a seamless transition and successful onboarding for your new leader.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_integration.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_integration.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701280/img/process_integration.png-wvc-sizes",
  alt: "Handshake between two professionals in a corporate environment"
}];
function StepRow({
  step,
  index
}: {
  step: ProcessStep;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const delay = `${index * 200}ms`;
  return <div ref={ref} data-index={index} className="border-t border-border last:border-b">
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-8 md:gap-12 items-center px-4 md:px-8 py-12 md:py-16 md:min-h-[240px]">
        {}
        <div className="transition-all duration-700 ease-out group" style={{
        transitionDelay: delay,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(20px)"
      }}>
          <span className="font-serif italic text-6xl md:text-[5rem] leading-none text-muted-foreground/60 hover:text-primary transition-colors duration-500" style={{
          WebkitTextStroke: "1px currentColor",
          color: "transparent"
        }}>
            {step.number}
          </span>
        </div>

        {}
        <div className="md:max-w-md transition-all duration-700 ease-out" style={{
        transitionDelay: delay,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <h3 className="font-default font-semibold text-3xl md:text-[2rem] leading-tight text-foreground mb-3">
            {step.title}
          </h3>
          <p className="font-default text-muted-foreground text-base leading-relaxed">
            {step.description}
          </p>
        </div>

        {}
        <div className="w-full md:w-72 lg:w-80 transition-all duration-700 ease-out overflow-hidden" style={{
        transitionDelay: `calc(${delay} + 200ms)`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateX(0)" : "translateX(40px)"
      }}>
          <div className="relative aspect-square overflow-hidden rounded-lg">
            <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-700" />
          </div>
        </div>
      </div>
    </div>;
}
export default function ExecutiveSearchProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4071" id="executive-search-process" className="bg-background text-foreground py-24 md:py-32 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className="text-center mb-20 md:mb-28 transition-all duration-1000 ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <p className="font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-primary mb-8">
            02 &mdash; Our Methodology
          </p>
          <h2 className="font-default font-serif text-4xl md:text-6xl lg:text-[5rem] leading-[1.05] text-foreground max-w-4xl mx-auto">
            Precision search,{" "}
            <span className="italic text-primary">proven results.</span>
          </h2>
        </div>

        {}
        <div className="w-full">
          {steps.map((step, i) => <StepRow key={step.number} step={step} index={i} />)}
        </div>
      </div>
    </section>;
}